/**
 * @file BinContentsSheet.tsx
 * @description Шторка «Содержимое ячейки» (phase19.14, W14; «Положить товар»,
 * перемещение и «Убрать из ячейки» — phase19.23, A4).
 *
 * Показывает партии с адресом этой ячейки: фасовка, номер партии, срок,
 * физический остаток, резерв и доступно. Количеств на уровне ячейки в системе
 * нет — это остаток партии на складе ячейки, о чём сказано подписью.
 *
 * Действия по строке — в меню «⋯»: переставить в другую ячейку или убрать
 * адрес (партия останется на складе, но без места хранения).
 *
 * Диалоги («Положить товар», «Переместить») рендерятся ВНУТРИ шторки осознанно
 * (проверено на base-ui 1.5.0, phase19.23 этап A): `useDialogRoot` считает
 * вложенность по React-контексту, который проходит сквозь портал, и у
 * родителя ставит `escapeKey: isTopmost === false`. То есть Esc закрывает
 * только верхний диалог, а фокус возвращается в шторку. Если вынести диалог
 * на уровень страницы, два Root'а станут соседями, оба останутся «верхними» —
 * и один Esc закроет заодно шторку.
 */

import { Boxes, Loader2, MoveRight, PackagePlus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { PutStockDialog } from "./PutStockDialog";
import { MoveLotBinDialog } from "../MoveLotBinDialog";
import { useWmsConfirm } from "../use-wms-confirm";
import { WmsConfirmDialog } from "../WmsConfirmDialog";
import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import { EmptyState } from "../../ui/EmptyState";
import { useNotify } from "../../ui/NotifyProvider";
import { RowActionsMenu } from "../../ui/RowActionsMenu";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../ui/sheet";
import { binsApi, type BinContentRowDto, type StorageBinDto } from "../../../lib/api-bins";
import { wmsErrorText } from "../../../lib/wms-error-text";

interface Props {
  bin: StorageBinDto;
  /** Содержимое изменилось — обновить список ячеек со сводками. */
  onChanged?: () => void;
  onClose: () => void;
}

export function BinContentsSheet({ bin, onChanged, onClose }: Props) {
  const notify = useNotify();
  const asker = useWmsConfirm();
  const [rows, setRows] = useState<BinContentRowDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [moving, setMoving] = useState<BinContentRowDto | null>(null);
  const [putting, setPutting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    binsApi
      .contents(bin.id)
      .then((data) => {
        if (cancelled) return;
        setRows(data);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(wmsErrorText(err, "Не удалось загрузить содержимое"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bin.id, reloadKey]);

  /** Обновляем и шторку, и список ячеек: сводка на карточке поменялась. */
  function refresh() {
    setReloadKey((n) => n + 1);
    onChanged?.();
  }

  /** «Убрать из ячейки» = снять адрес партии; остаток на складе не меняется. */
  async function handleRemove(row: BinContentRowDto) {
    // Окно кабинета вместо системного `confirm()` (phase19.31).
    const ok = await asker.confirm({
      title: `Убрать партию ${row.batchNumber} из ячейки ${bin.code}?`,
      description: "Остаток на складе останется, но место хранения будет не задано.",
      action: "Убрать",
      danger: true,
    });
    if (!ok) return;
    try {
      await binsApi.moveLot(row.lotId, null);
      notify.success("Партия убрана из ячейки");
      refresh();
    } catch (err) {
      notify.error(wmsErrorText(err, "Не удалось убрать партию из ячейки"));
    }
  }

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader className="border-b border-outline-variant px-4 py-3">
          <SheetTitle>Ячейка {bin.code}</SheetTitle>
        </SheetHeader>
        <div className="flex h-[calc(100vh-58px)] flex-col gap-3 overflow-y-auto p-4">
          <p className="text-xs text-on-surface-variant">
            {bin.label ?? "Содержимое ячейки"}. Количество — остаток партии на
            складе этой ячейки: поштучного учёта по ячейкам пока нет.
          </p>

          <Button variant="outline" className="self-start" onClick={() => setPutting(true)}>
            <PackagePlus className="size-5" />
            <span className="ml-1.5">Положить товар</span>
          </Button>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="size-5 animate-spin text-primary" />
            </div>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={Boxes}
              title="Ячейка пустая"
              description="Партий с этим адресом нет — положите товар, чтобы задать место хранения."
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {rows.map((row) => (
                <li
                  key={row.lotId}
                  className="rounded-xl border border-on-surface/8 p-3 text-sm"
                >
                  <div className="font-medium">{row.sku.productName}</div>
                  <div className="font-mono text-xs text-on-surface-variant">
                    {row.sku.displayName ? `${row.sku.displayName} · ` : ""}
                    {row.sku.code}
                  </div>
                  <div className="mt-1 text-xs text-on-surface-variant">
                    Партия {row.batchNumber}
                    {row.expiresAt
                      ? ` · до ${new Date(row.expiresAt).toLocaleDateString("ru-RU")}`
                      : ""}
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="text-xs tabular-nums text-on-surface-variant">
                      физич. {row.quantity} · резерв {row.reserved} · доступно{" "}
                      {row.available}
                    </span>
                    <RowActionsMenu
                      size="compact"
                      title={`Партия ${row.batchNumber}`}
                      ariaLabel={`Действия с партией ${row.batchNumber}`}
                      actions={[
                        {
                          key: "move",
                          label: "Переместить в другую ячейку",
                          icon: MoveRight,
                          run: () => setMoving(row),
                        },
                        {
                          key: "unbind",
                          label: "Убрать из ячейки",
                          icon: Trash2,
                          danger: true,
                          run: () => void handleRemove(row),
                        },
                      ]}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {putting ? <PutStockDialog bin={bin} onPlaced={refresh} onClose={() => setPutting(false)} /> : null}
        {/* Внутри шторки — по тому же правилу вложенных окон (см. шапку). */}
        <WmsConfirmDialog request={asker.request} onSettle={asker.settle} />

        {moving ? (
          <MoveLotBinDialog
            lotId={moving.lotId}
            batchNumber={moving.batchNumber}
            warehouseId={bin.warehouseId}
            currentBinCode={bin.code}
            onMoved={refresh}
            onClose={() => setMoving(null)}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
